import { toast } from 'react-hot-toast';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getBrandStrategy, updateCustomerPersona } from '../services';
import { useBrandStrategyStore } from '../store/brand-strategy-store';
import { CustomerPersona } from '../types';
import { createStableQueryOptions } from '../utils/query-options';

export const useGetCustomerPersonas = (projectId: string) => {
  const fields =
    'customer_personas,customer_pain_points,customer_pain_point_solutions';
  return useQuery({
    queryKey: ['customer-personas', projectId],
    queryFn: () => getBrandStrategy(projectId, fields),
    ...createStableQueryOptions(),
  });
};

export const useUpdateCustomerPersona = (projectId: string) => {
  const { brandStrategyId } = useBrandStrategyStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CustomerPersona) => {
      if (!brandStrategyId) {
        throw new Error('Brand strategy ID is required');
      }

      if (!data.id) {
        throw new Error('Customer persona ID is required');
      }

      const response = await updateCustomerPersona(
        projectId,
        brandStrategyId,
        data.id,
        data
      );
      return response.data;
    },
    onSuccess: (data) => {
      // Update the cache with the new data
      queryClient.setQueryData(
        ['customer-personas', projectId],
        (oldData: any) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              customer_personas: oldData.data.customer_personas.map(
                (persona: CustomerPersona) =>
                  persona.id === data.id ? data : persona
              ),
            },
          };
        }
      );
      // Also invalidate the main brand strategy query
      queryClient.invalidateQueries({ queryKey: ['brandStrategy', projectId] });
    },
    onError: (error) => {
      console.error('Error updating customer persona:', error);
      toast.error('Failed to update customer persona');
    },
  });
};
